import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Boxes, Building2, Globe, Network, Search } from 'lucide-react';
import { api } from '../api/cliente.js';

interface Resultado {
  tipo: 'ip' | 'bloco' | 'zona' | 'operadora';
  titulo: string;
  subtitulo: string;
  destino: string;
  ptr?: string | null;
}

const ICONES = {
  ip: Network,
  bloco: Boxes,
  zona: Globe,
  operadora: Building2,
};

/**
 * Busca da topbar.
 *
 * Antes era um campo decorativo — desenhado e nunca ligado a nada. Agora é o
 * caminho mais curto do painel: digite o endereço que alguém reclamou e vá
 * direto a ele, sem passar por Blocos e sem paginar.
 */
export function BuscaGlobal() {
  const navegar = useNavigate();
  const [termo, setTermo] = useState('');
  const [debounce, setDebounce] = useState('');
  const [aberto, setAberto] = useState(false);
  const [indice, setIndice] = useState(0);
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounce(termo), 250);
    return () => clearTimeout(t);
  }, [termo]);

  // Fecha ao clicar fora. Sem isso a lista fica pendurada sobre a tela toda
  // vez que alguém desiste da busca.
  useEffect(() => {
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  const { data: resultados = [] } = useQuery({
    queryKey: ['busca', debounce],
    queryFn: () => api.get<Resultado[]>(`/busca?q=${encodeURIComponent(debounce)}`),
    enabled: debounce.trim().length >= 2,
  });

  function ir(r: Resultado) {
    navegar(r.destino);
    setTermo('');
    setAberto(false);
  }

  return (
    <div ref={caixa} className="relative mx-auto hidden max-w-md flex-1 lg:block">
      <Search
        size={16}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-texto-mudo"
      />
      <input
        value={termo}
        onChange={(e) => {
          setTermo(e.target.value);
          setAberto(true);
          setIndice(0);
        }}
        onFocus={() => setAberto(true)}
        onKeyDown={(e) => {
          // Setas e Enter: quem chega com um IP na mão digita e aperta Enter,
          // sem tirar a mão do teclado.
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setIndice((i) => Math.min(i + 1, resultados.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setIndice((i) => Math.max(i - 1, 0));
          } else if (e.key === 'Enter' && resultados[indice]) {
            ir(resultados[indice]);
          } else if (e.key === 'Escape') {
            setAberto(false);
          }
        }}
        type="search"
        placeholder="Buscar IP, bloco, zona ou operadora…"
        className="h-10 w-full rounded-controle border border-borda bg-fundo pl-9 pr-3 font-mono text-[13px]
          placeholder:font-sans placeholder:text-texto-mudo focus:border-marca focus:bg-superficie focus:outline-none"
      />

      {aberto && debounce.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-12 z-30 overflow-hidden rounded-card border border-borda bg-superficie shadow-hover">
          {resultados.length === 0 ? (
            <p className="px-4 py-3 text-[13px] text-texto-suave">Nada encontrado.</p>
          ) : (
            <ul>
              {resultados.map((r, i) => {
                const Icone = ICONES[r.tipo];
                return (
                  <li key={`${r.tipo}-${r.titulo}-${i}`}>
                    <button
                      onClick={() => ir(r)}
                      onMouseEnter={() => setIndice(i)}
                      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left ${
                        i === indice ? 'bg-tint' : 'hover:bg-borda-tenue'
                      }`}
                    >
                      <Icone size={16} className="shrink-0 text-texto-mudo" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-mono text-[13px] font-semibold">
                          {r.titulo}
                        </span>
                        <span className="block truncate text-[11.5px] text-texto-suave">
                          {r.subtitulo}
                        </span>
                      </span>
                      {/* Para IP, já mostra o reverso: metade das buscas
                          termina aqui, sem precisar abrir tela nenhuma. */}
                      {r.tipo === 'ip' && (
                        <span className="shrink-0 truncate font-mono text-[11.5px] text-marca">
                          {r.ptr ?? 'sem reverso'}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
